import { useState, useCallback, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import { useCreateCaseMutation, initDraft, clearDraft } from '../../../services/MedNecService';
import type { MedNecCaseView } from '../../../services/MedNecService/types/medNecTypes';

export interface IntakeFormErrors {
  [key: string]: string | undefined;
  firstName?: string;
  lastName?: string;
  dateOfBirth?: string;
  memberId?: string;
  payer?: string;
  physician?: string;
  npi?: string;
  cptCodes?: string;
  icdCodes?: string;
}

function validate(draft: MedNecCaseView): IntakeFormErrors {
  const errors: IntakeFormErrors = {};
  const patient = draft.patient;

  if (!patient?.firstName?.trim()) errors.firstName = 'First name is required';
  if (!patient?.lastName?.trim()) errors.lastName = 'Last name is required';
  if (!patient?.dateOfBirth) errors.dateOfBirth = 'Date of birth is required';
  if (!patient?.memberId?.trim()) errors.memberId = 'Member ID is required';

  if (!draft.payerId.trim()) errors.payer = 'Payer is required';
  if (!draft.physicianName.trim()) errors.physician = 'Physician is required';

  if (!draft.physicianNpi.trim()) {
    errors.npi = 'NPI is required';
  } else if (!/^\d{10}$/.test(draft.physicianNpi.trim())) {
    errors.npi = 'NPI must be exactly 10 digits';
  }

  if (draft.cptCodes.length === 0) errors.cptCodes = 'At least one CPT code is required';
  if (!draft.icdCodes || draft.icdCodes.length === 0) errors.icdCodes = 'At least one ICD-10 code is required';

  return errors;
}

export function useCaseIntakeForm(center: string) {
  const dispatch = useAppDispatch();
  const draft = useAppSelector(state => state.medNecCase.draftCase);
  const [errors, setErrors] = useState<IntakeFormErrors>({});
  const [createCase, { isLoading: isSubmitting, data: createdCase, error: submitError, isSuccess }] = useCreateCaseMutation();

  useEffect(() => {
    dispatch(initDraft(center));
    return () => { dispatch(clearDraft()); };
  }, [dispatch, center]);

  const handleSubmit = useCallback(() => {
    if (!draft) return;

    const validationErrors = validate(draft);
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) return;

    const view: Partial<MedNecCaseView> = {
      patient: draft.patient ? {
        firstName: draft.patient.firstName.trim(),
        lastName: draft.patient.lastName.trim(),
        dateOfBirth: draft.patient.dateOfBirth,
        memberId: draft.patient.memberId.trim(),
        middleInitial: draft.patient.middleInitial,
        sex: draft.patient.sex,
        ssn4: draft.patient.ssn4,
        address: draft.patient.address,
        city: draft.patient.city,
        state: draft.patient.state,
        zip: draft.patient.zip,
        homePhone: draft.patient.homePhone,
        cellPhone: draft.patient.cellPhone,
      } : null,
      payerId: draft.payerId.trim(),
      payerName: draft.payerName.trim(),
      facilityCode: draft.facilityCode,
      physicianName: draft.physicianName.trim(),
      physicianNpi: draft.physicianNpi.trim(),
      cptCodes: draft.cptCodes,
      icdCodes: draft.icdCodes,
      levelOfCare: draft.levelOfCare,
      notes: draft.notes?.trim() || null,
      insurance: draft.insurance,
      surgery: draft.surgery,
      guarantor: draft.guarantor,
      preop: draft.preop,
      procedures: draft.procedures,
      fullTextBag: draft.fullTextBag,
      imagingSummary: draft.imagingSummary,
    };

    createCase({ view });
  }, [draft, createCase]);

  const reset = useCallback(() => {
    dispatch(initDraft(center));
    setErrors({});
  }, [dispatch, center]);

  return {
    errors,
    isSubmitting,
    isSuccess,
    createdCase,
    submitError,
    handleSubmit,
    reset,
  };
}
